import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import WmsFullScreenLayout from '../shared/WmsFullScreenLayout';
import useWmsSession from '../shared/useWmsSession';
import WmsScreenAccessGate from '../shared/WmsScreenAccessGate';
import { screenService } from '../services/screenService';
import packingService, { PackingOrderDto, PackingStats, CartPackingResult } from '../services/packingService';
import { PackingModeId } from './packing/packingModes';
import PackingHome from './packing/PackingHome';
import PackingCartScan from './packing/PackingCartScan';
import PackingCartOrders from './packing/PackingCartOrders';
import PackingCartCompleted from './packing/PackingCartCompleted';
import PackingScanEntry from './packing/PackingScanEntry';
import PackingLabelPrint from './packing/PackingLabelPrint';

type PackingPhase =
  | 'home'
  | 'cart-scan'
  | 'cart-orders'
  | 'scan-entry'
  | 'label-print'
  | 'cart-completed';

const DEFAULT_PACKING_PERMISSIONS = {
  canPicking: false,
  canPacking: true,
  canReceiving: false,
  canCycleCount: false,
  pickingScreenId: null,
  packingScreenId: null,
  receivingScreenId: null,
  cycleCountScreenId: null,
};

const WmsPackingScreen: React.FC = () => {
  const { screenId } = useParams<{ screenId: string }>();
  const { session, logout } = useWmsSession();

  const [phase, setPhase] = useState<PackingPhase>('home');
  const [packingMode, setPackingMode] = useState<PackingModeId>('product');
  const [warehouseId, setWarehouseId] = useState('');
  const [loadingScreen, setLoadingScreen] = useState(true);
  const [todayPacked, setTodayPacked] = useState(0);

  const [currentOrder, setCurrentOrder] = useState<PackingOrderDto | null>(null);

  // Araba modu durumu.
  const [cartCode, setCartCode] = useState('');
  const [cartSource, setCartSource] = useState<string | undefined>();
  const [cartOrders, setCartOrders] = useState<PackingOrderDto[]>([]);
  const [cartTotal, setCartTotal] = useState(0);

  useEffect(() => {
    if (!screenId) return;
    (async () => {
      try {
        const res = await screenService.getScreenDetail('packing', screenId);
        const data = res.data?.data || res.data;
        if (data?.warehouseId) setWarehouseId(data.warehouseId);
      } catch { /* screen not found */ }
      setLoadingScreen(false);
    })();
  }, [screenId]);

  const refreshStats = useCallback(async () => {
    if (!warehouseId) return;
    try {
      const stats: PackingStats = await packingService.getStats(warehouseId);
      setTodayPacked(stats.todayPacked);
    } catch { /* ignore */ }
  }, [warehouseId]);

  useEffect(() => {
    if ((phase === 'home' || phase === 'scan-entry') && warehouseId) refreshStats();
  }, [phase, warehouseId, refreshStats]);

  const resetCart = useCallback(() => {
    setCartCode('');
    setCartSource(undefined);
    setCartOrders([]);
    setCartTotal(0);
  }, []);

  const handleLogout = useCallback(() => {
    logout();
    setCurrentOrder(null);
    resetCart();
    setPhase('home');
  }, [logout, resetCart]);

  const handleSelectMode = useCallback((mode: PackingModeId) => {
    setPackingMode(mode);
    setCurrentOrder(null);
    if (mode === 'cart') {
      resetCart();
      setPhase('cart-scan');
    } else {
      setPhase('scan-entry');
    }
  }, [resetCart]);

  const handleCartFound = useCallback((result: CartPackingResult) => {
    setCartCode(result.cartCode);
    setCartSource(result.marketplaceSource);
    setCartOrders(result.orders);
    setCartTotal(result.orders.length);
    setPhase('cart-orders');
  }, []);

  // Okutma / liste secimi: ara adimlar (order-found + packing) atlaniyor —
  // dogrudan etiket yazdirma. completePacking yazdirinca Packed yapar.
  const goToLabelPrint = useCallback(async (order: PackingOrderDto, _source?: 'product' | 'slot', _sc?: string) => {
    let next = order;
    if (!order.items || order.items.length === 0) {
      try {
        const detailed = await packingService.getOrder(order.id);
        if (detailed) next = detailed;
      } catch { /* keep order */ }
    }
    setCurrentOrder(next);
    try {
      await packingService.startPacking(next.id);
    } catch { /* ignore — Packed yeniden acilisinda da zararsiz */ }
    setPhase('label-print');
  }, []);

  const handleSelectCartOrder = useCallback((order: PackingOrderDto) => {
    goToLabelPrint(order);
  }, [goToLabelPrint]);

  // Araba listesinde barkod okutulunca: siparis eslesir ve dogrudan etiket.
  const handleScanCartOrder = useCallback(async (order: PackingOrderDto) => {
    await goToLabelPrint(order);
  }, [goToLabelPrint]);

  // Bugun tamamlanan (Packed) listesinden yeniden ac — etiket + MNG aktarimi.
  const handleReopenCompleted = useCallback(async (orderId: string) => {
    try {
      const detailed = await packingService.getOrder(orderId);
      if (!detailed) return;
      await goToLabelPrint(detailed);
    } catch { /* ignore */ }
  }, [goToLabelPrint]);

  const handleLabelComplete = useCallback(() => {
    // Etiket yuklenemedi vb. geri donus: moda gore geri git.
    if (packingMode === 'cart') setPhase('cart-orders');
    else setPhase('scan-entry');
  }, [packingMode]);

  const handlePrintDone = useCallback(() => {
    const finishedId = currentOrder?.id;
    setCurrentOrder(null);
    refreshStats();

    if (packingMode === 'cart') {
      const remaining = cartOrders.filter((o) => o.id !== finishedId);
      setCartOrders(remaining);
      if (remaining.length === 0) {
        setPhase('cart-completed');
      } else {
        setPhase('cart-orders');
      }
    } else {
      setPhase('scan-entry');
    }
  }, [currentOrder, packingMode, cartOrders, refreshStats]);

  const handleBackHome = useCallback(() => {
    setCurrentOrder(null);
    resetCart();
    setPhase('home');
  }, [resetCart]);

  const content = loadingScreen ? (
    <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f7fb' }}>
      <CircularProgress />
    </Box>
  ) : (
    <WmsFullScreenLayout
      currentScreen="packing"
      cashierName={session?.cashierName || ''}
      wmsPermissions={session?.wmsPermissions || DEFAULT_PACKING_PERMISSIONS}
      onLogout={handleLogout}
    >
      {phase === 'home' && (
        <PackingHome
          personnelName={session?.personnelName || session?.cashierName || ''}
          warehouseId={warehouseId}
          onSelectMode={handleSelectMode}
        />
      )}

      {phase === 'cart-scan' && (
        <PackingCartScan
          warehouseId={warehouseId}
          onCartFound={handleCartFound}
          onBack={handleBackHome}
        />
      )}

      {phase === 'cart-orders' && (
        <PackingCartOrders
          cartCode={cartCode}
          marketplaceSource={cartSource}
          orders={cartOrders}
          totalCount={cartTotal}
          onSelectOrder={handleSelectCartOrder}
          onScanOrder={handleScanCartOrder}
          onBack={handleBackHome}
        />
      )}

      {phase === 'cart-completed' && (
        <PackingCartCompleted
          cartCode={cartCode}
          packedCount={cartTotal}
          onScanAnother={() => { resetCart(); setPhase('cart-scan'); }}
          onHome={handleBackHome}
        />
      )}

      {phase === 'scan-entry' && (
        <PackingScanEntry
          mode={packingMode === 'slot' ? 'slot' : 'product'}
          cashierName={session?.cashierName || ''}
          warehouseId={warehouseId}
          todayPacked={todayPacked}
          onOrderFound={goToLabelPrint}
          onReopenCompleted={handleReopenCompleted}
          onBack={handleBackHome}
        />
      )}

      {phase === 'label-print' && currentOrder && (
        <PackingLabelPrint
          orderId={currentOrder.id}
          alreadyPacked={(currentOrder.status || '').toLowerCase() === 'packed'}
          onComplete={handleLabelComplete}
          onPrintDone={handlePrintDone}
          onBack={() => setPhase(packingMode === 'cart' ? 'cart-orders' : 'scan-entry')}
        />
      )}
    </WmsFullScreenLayout>
  );

  return (
    <WmsScreenAccessGate screenType="packing" screenId={screenId || ''}>
      {content}
    </WmsScreenAccessGate>
  );
};

export default WmsPackingScreen;
